import {
  CohortSampleContextType,
  IBuilderCohortConfig,
  IBuilderExpandEvent,
  IBuilderFoldEvent,
  IBuilderLlmContext,
  IBuilderMergeEvent,
  IBuilderSampledCohort,
  ICohortRow,
  IMeasureRow,
  IPopulateQueryBuilder,
  IQueryMeasureFormula,
  IReportFilters,
  IRetentionFields,
  IRow,
  IStep,
  LookForwardWindowRequirement,
  RowType,
} from './types';
import {
  FunnelOrderKey,
  IEventConditionFieldsFragment,
  IMeasureFunction,
  IPartitionFieldsFragment,
  IPartitionValueFieldsFragment,
  ISubject,
  ITimeWindow,
  MeasureType,
  QueryType,
  SimpleCohortTypeKey,
  TimeUnit,
  TimeWindowKey,
} from '../../../utils/apollo/graphqlSchema';
import { freeze } from '@reduxjs/toolkit';
import { defaultBreakdown } from './breakdown/defaults';
import { defaultDateInput } from './date/defaults';

export const defaultMeasureFunction = (): IMeasureFunction => ({
  __typename: 'MeasureFunction',
  name: '',
  key: '',
  fieldRequired: false,
  requirePercentile: false,
  forceBreakdownOverall: false,
});

export const defaultMeasureRow = (): IMeasureRow => ({
  type: MeasureType.Simple,
  measureId: '',
  showMeasure: false,
  percentile: undefined,
  measureFilter: null,
  measureRollingControl: undefined,
  isCollapsedMeasure: false,
  defaultNamedMeasure: null,
  showNamedCohort: false,
});

export const defaultCohortRow = (): ICohortRow => ({
  ...defaultMeasureRow(),
  fn: defaultMeasureFunction(),
  cohortId: '',
  userAliasId: '',
  isActive: false,
  simpleCohortTypeKey: SimpleCohortTypeKey.Performed,
  subject: '',
  timeWindow: '',
  timeWindowValues: [null, null],
  operator: '',
  returnedOperator: '',
  operatorValues: [null, null],
  eventConditions: [defaultEventCondition()],
  analysisId: '',
  datePicker: null,
  isCompoundCohort: false,
  conditionFilter: null,
  percentileFilter: null,
  showCohort: false,
  isExpanded: false,
  defaultNamedCohort: null,
  contextInput: {
    __typename: 'CohortRefProps',
    samplePercentage: 100,
    dateRange: null,
  },
  cohortDurationStart: { required: false },
  lookForwardWindow: { required: LookForwardWindowRequirement.NotRequired },
  aliasReplacements: [],
  timeToConvert: null,
});

export const defaultRow = (): IRow => ({
  ...defaultMeasureRow(),
  eventConditions: [defaultEventCondition()],
  fn: defaultMeasureFunction(),
  userAliasId: '',
  cohortFilter: defaultCohortRow(),
});

export const defaultSampledCohort = (): IBuilderSampledCohort => ({
  id: '',
  name: '',
  isCompoundCohort: false,
  operations: [],
  defaultOperation: null,
  sampleOperation: null,
  samplingRate: {
    type: CohortSampleContextType.Percentage,
    samplePercentage: 100,
    sampleAbsolute: 1000,
  },
});

const defaultSubject = (): ISubject => ({
  __typename: 'Subject',
  defaultFunnelOrder: {
    __typename: 'FunnelOrder',
    key: FunnelOrderKey.Any,
    name: '',
  },
  funnelOrders: [],
  key: '',
  name: '',
});

const defaultTimeWindow = (): ITimeWindow => ({
  __typename: 'TimeWindow',
  key: TimeWindowKey.Current,
  name: '',
});

const defaultCohortTypeConfig = () => ({
  defaultSubject: defaultSubject(),
  defaultTimeWindow: defaultTimeWindow(),
  operators: [],
  subjects: [],
  timeWindows: [],
});

export const defaultCohortConfig = (): IBuilderCohortConfig => ({
  defaultSamplingRate: 100,
  simpleCohortTypes: [],
  expressionConfig: {
    __typename: 'ExpressionCohortConfig',
    ...defaultCohortTypeConfig(),
    percentileOperators: [],
  },
  funnelConfig: {
    __typename: 'FunnelCohortConfig',
    ...defaultCohortTypeConfig(),
    ttcConfig: {
      __typename: 'TimeToConvertCohortConfig',
      timeUnits: [],
      operators: [],
    },
  },
  measureConfig: {
    __typename: 'MeasureCohortConfig',
    ...defaultCohortTypeConfig(),
    functions: [],
    percentileOperators: [],
  },
  performedConfig: {
    __typename: 'PerformedCohortConfig',
    ...defaultCohortTypeConfig(),
  },
  retentionConfig: {
    __typename: 'RetentionCohortConfig',
    ...defaultCohortTypeConfig(),
    returnedOperators: [],
  },
  logicOps: [],
});

export const defaultPrediction = () => ({
  timeWindow: 7,
  eventCondition: {
    anyEvent: null,
    events: [],
    virtualEvents: [],
  },
});

export const defaultEventCondition = (): IEventConditionFieldsFragment => ({
  events: [],
  filter: null,
  field: null,
  __typename: 'EventCondition',
});

export const defaultRetention = freeze<IRetentionFields>(
  {
    retentionConditions: [[defaultEventCondition()], [defaultEventCondition()]],
    activeRetentionUnit: TimeUnit.Day,
    customTimeBrackets: [],
  },
  true,
);

/**
 * Kobol doesn't provide value below for step conversion, therefore declare here.
 */
export const stepPartitionOptions = [
  {
    __typename: 'NonePartition',
    key: 'None',
    name: 'None',
  },
  {
    __typename: 'ConversionWindowPartition',
    key: 'ConversionWindow',
    name: 'Conversion Window',
  },
] as const satisfies readonly [
  IPartitionFieldsFragment,
  IPartitionFieldsFragment,
];

type IStepPartition = (typeof stepPartitionOptions)[number];

export const partitionDictionary: Record<
  IStepPartition['key'],
  IStepPartition
> = {
  None: stepPartitionOptions[0],
  ConversionWindow: stepPartitionOptions[1],
} as const;

export const defaultStepPartitionValue = freeze<IPartitionValueFieldsFragment>(
  {
    __typename: 'PartitionValue',
    partition: partitionDictionary.None,
    conversionWindow: null,
  },
  true,
);

export const defaultStep = (): IStep => ({
  name: '',
  eventConditions: [defaultEventCondition()],
  negativeSteps: [],
  userAliasId: null,
  partitionValue: { ...defaultStepPartitionValue },
  showPartition: false,
});

export const defaultMergeEvent = (): IBuilderMergeEvent => ({
  isActive: true,
  name: null,
  isNameAutoGenerated: true,
  eventConditions: [defaultEventCondition()],
});

export const defaultExpandEvent = (): IBuilderExpandEvent => ({
  isActive: true,
  eventCondition: defaultEventCondition(),
  field: null,
});

export const defaultFoldEvent = (): IBuilderFoldEvent => ({
  isActive: true,
  eventCondition: defaultEventCondition(),
});

export const defaultReportFilters = freeze<IReportFilters>(
  {
    filter: undefined,
    [RowType.breakdownCohorts]: [],
    [RowType.globalCohorts]: [],
    breakdown: defaultBreakdown,
  },
  true,
);

export const defaultPopulateQueryAnalysisFields = freeze<IPopulateQueryBuilder>(
  {
    ...defaultReportFilters,
    queryType: QueryType.Expression,
    alias: '',
    formulaSamplingRate: null,
    activeSchemaId: '',
    timeWindow: null,
    sourceId: null,
    additionalSettings: [],
    selectedOptions: {
      type: QueryType.Expression,
    },
    compoundMeasureUserAliasId: undefined,
    trigger: defaultEventCondition(),
    resultFilter: null,
    compoundMeasureName: null,
    compoundMeasureId: null,
    measureFormula: null,
    comparisonConfig: null,
    dateInput: defaultDateInput,
    binning: null,
    [RowType.measures]: [defaultRow()],
  },
  true,
);

export const defaultMeasureFormula = freeze<IQueryMeasureFormula>(
  {
    expression: null,
    validationError: null,
    isValid: true, // formula is optional - therefore valid by default
  },
  true,
);

export const defaultLlmContext = freeze<IBuilderLlmContext>({
  previousMessages: [],
  // XXX by default, the prompt is visible on a new report on or reset.
  // But is collapsed on new a report loaded
  isPromptVisible: true,
});
